import React, { Fragment, memo, type RefObject, useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { token } from '@atlaskit/tokens';
import { ScreenReaderCaption } from '@atlassian/jira-accessibility';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useScrollState } from '@atlassian/jira-software-fast-virtual';
import {
	COLUMN_ID_MEATBALL_MENU,
	MOVE_COLUMN_FIRST,
	MOVE_COLUMN_LAST,
	MOVE_COLUMN_LEFT,
	MOVE_COLUMN_RIGHT,
} from '../../../common/constants';
import type {
	Column,
	ReorderColumn,
	TargetColumnPosition,
	ColumnResizeHandleType,
} from '../../../common/types';
import { useTableSize } from '../../../controllers/table-size';
import { BaseHeaderCellContainer, HeaderCell, HeaderCellContainer } from './header-cell';
import messages from './messages';

type Props = {
	columns: Column[];
	canViewColumnConfiguration: boolean;
	canEditColumnConfiguration: boolean;
	hasFixedTableLayout: boolean;
	isColumnResizingEnabled: boolean;
	resizeHandleType?: ColumnResizeHandleType;
	onColumnsChange?: (columns: Column[]) => void;
	onResizeColumn: (fieldSetId: string, width: number | undefined) => void;
	tableRef?: RefObject<HTMLTableElement>;
};

/**
 * Runs a flash animation on the background color of the column that has been reordered,
 * in order to highlight where the column has moved to.
 */
const triggerColumnFlash = (table: HTMLElement, index: number) => {
	const columnCellsSelector = `th:nth-of-type(${index + 1}), td:nth-of-type(${index + 1})`;

	Array.from(table.querySelectorAll(columnCellsSelector))
		.filter((element): element is HTMLElement => element instanceof HTMLElement)
		.forEach((element) => triggerPostMoveFlash(element));
};

type ReorderOperation = {
	finishIndex: number;
};

export const Header = memo<Props>(
	({
		columns,
		canViewColumnConfiguration,
		canEditColumnConfiguration,
		hasFixedTableLayout,
		isColumnResizingEnabled,
		resizeHandleType,
		onColumnsChange,
		onResizeColumn,
		tableRef,
	}: Props) => {
		const { formatMessage } = useIntl();

		const { createAnalyticsEvent } = useAnalyticsEvents();

		// Unique identifier for the header of this particular issue table instance
		const [instanceId] = useState(() => Symbol('pdnd-issue-table-header'));

		const [lastReorderOperation, setLastReorderOperation] = useState<ReorderOperation | null>(null);

		const [screenReaderMessage, setScreenReaderMessage] = useState('');

		const reorderColumn: ReorderColumn = useCallback(
			({ startIndex, indexOfTarget, closestEdgeOfTarget = null, analyticsAttributes = {} }) => {
				const finishIndex = getReorderDestinationIndex({
					axis: 'horizontal',
					startIndex,
					indexOfTarget,
					closestEdgeOfTarget,
				});

				if (startIndex === finishIndex) {
					return;
				}

				const newColumns = reorder({
					list: columns,
					startIndex,
					finishIndex,
				});

				onColumnsChange?.(newColumns);

				setLastReorderOperation({ finishIndex });

				setScreenReaderMessage(
					formatMessage(messages.movedColumn, {
						column: columns[startIndex]?.title,
						previousPosition: startIndex + 1,
						position: finishIndex + 1,
						// TODO: EM-7165 update to columns.length when excluding column picker
						total: columns.length - 1,
					}),
				);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'reordered',
						actionSubject: 'columns',
					}),
					'issueTable',
					analyticsAttributes,
				);
			},
			[columns, createAnalyticsEvent, formatMessage, onColumnsChange],
		);

		// We use an object for lastReorderOperation so we can trigger the column flash
		// if the finish index is the same as the previous reorder operation.
		useEffect(() => {
			const table = tableRef?.current;

			if (table && lastReorderOperation !== null) {
				triggerColumnFlash(table, lastReorderOperation.finishIndex);
			}
		}, [lastReorderOperation, tableRef]);

		useEffect(() => {
			if (!canEditColumnConfiguration) {
				return;
			}

			return monitorForElements({
				canMonitor({ source }) {
					// Only monitor this particular instance id
					// (i.e. prevent monitoring entities that are not headers, or headers from another issue table)
					return source.data.instanceId === instanceId;
				},
				onDrop({ source, location }) {
					const [destination] = location.current.dropTargets;

					if (!destination) {
						return;
					}

					const startIndex = columns.findIndex((column) => column.id === source.data.id);

					if (startIndex === -1) {
						return;
					}

					const indexOfTarget = columns.findIndex((column) => column.id === destination.data.id);

					if (indexOfTarget === -1) {
						return;
					}

					const closestEdgeOfTarget = extractClosestEdge(destination.data);

					reorderColumn({
						startIndex,
						indexOfTarget,
						closestEdgeOfTarget,
						analyticsAttributes: { operation: 'drop' },
					});
				},
			});
		}, [columns, canEditColumnConfiguration, instanceId, reorderColumn, tableRef]);

		const onMoveColumn = (index: number, position: TargetColumnPosition) => {
			if (canEditColumnConfiguration && onColumnsChange !== undefined) {
				switch (position) {
					case MOVE_COLUMN_FIRST: {
						if (index > 0) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: 0,
								closestEdgeOfTarget: 'left',
								analyticsAttributes: { operation: 'move-first' },
							});
						}
						break;
					}
					case MOVE_COLUMN_LEFT: {
						if (index > 0) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: index - 1,
								closestEdgeOfTarget: 'left',
								analyticsAttributes: { operation: 'move-left' },
							});
						}
						break;
					}
					case MOVE_COLUMN_RIGHT: {
						// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
						if (index < columns.length - 2) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: index + 1,
								closestEdgeOfTarget: 'right',
								analyticsAttributes: { operation: 'move-right' },
							});
						}
						break;
					}
					case MOVE_COLUMN_LAST: {
						// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
						if (index < columns.length - 2) {
							reorderColumn({
								startIndex: index,
								// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
								indexOfTarget: columns.length - 2,
								closestEdgeOfTarget: 'right',
								analyticsAttributes: { operation: 'move-last' },
							});
						}
						break;
					}
					default:
						throw new Error(`Unexpected target column position ${position}`);
				}
			}
		};

		const onRemoveColumn = (index: number) => {
			if (canEditColumnConfiguration && onColumnsChange !== undefined) {
				const newColumns = [...columns];
				newColumns.splice(index, 1);
				onColumnsChange(newColumns);
			}
		};

		const { scrollTop, scrollLeft, width: containerWidth } = useScrollState();

		const { width: tableWidth } = useTableSize();

		const hasShadowBottom = scrollTop > 0;

		// Scroll measurements are fractional, while width measurements are integer, so we need to round before comparing
		const hasShadowLeft =
			containerWidth < tableWidth && Math.round(scrollLeft) < tableWidth - containerWidth;

		return (
			<>
				<ScreenReaderCaption
					aria-live="assertive"
					data-testid="native-issue-table.ui.issue-table.header.screen-reader-caption"
				>
					{screenReaderMessage}
				</ScreenReaderCaption>
				<StickyHeader hasShadow={hasShadowBottom}>
					<tr>
						{columns.map((column, index) => (
							// TODO: EM-7165 – refactor column picker logic, separating it from regular columns
							<Fragment key={column.id}>
								{
									// Empty cell to fill remainig space if if configured columns are not wide enough to fill the table
									ff('nin-resizable-columns_8z4eo') &&
										hasFixedTableLayout &&
										column.id === COLUMN_ID_MEATBALL_MENU && <EmptyHeaderCell aria-hidden />
								}
								{column.customHeader ? (
									<HeaderCellContainer
										isCustom
										scope="col"
										columnWidth={
											ff('nin-resizable-columns_8z4eo')
												? column.customWidth ?? column.defaultWidth
												: column.width
										}
										// aria-label is added to prevent the extra accessibility hints in the header cell from being
										// read as the column title when a user is navigating between columns in the table content.
										aria-label={column.title}
										isSticky={canViewColumnConfiguration}
										hasShadow={canViewColumnConfiguration && hasShadowLeft}
									>
										{column.customHeader}
									</HeaderCellContainer>
								) : (
									<HeaderCell
										column={column}
										hasColumnConfiguration={canEditColumnConfiguration}
										instanceId={instanceId}
										isColumnResizingEnabled={isColumnResizingEnabled}
										isFirst={index === 0}
										// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
										isLast={index === columns.length - 2}
										resizeHandleType={resizeHandleType}
										tableRef={tableRef}
										onMoveColumn={(position) => onMoveColumn(index, position)}
										onRemoveColumn={() => onRemoveColumn(index)}
										onResizeColumn={onResizeColumn}
									/>
								)}
							</Fragment>
						))}
					</tr>
				</StickyHeader>
			</>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StickyHeader = styled.thead<{ hasShadow: boolean }>(
	{
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasShadow }) =>
		hasShadow && {
			boxShadow: `
                0px 8px 8px -8px ${token('elevation.shadow.overflow.spread', '#091e4229')},
                0px 1px 1px -1px ${token('elevation.shadow.overflow.perimeter', '#091e421f')}
            `,
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyHeaderCell = styled(BaseHeaderCellContainer)({
	borderLeftWidth: 0,
	borderRightWidth: 0,
});
